/**
 * AvatarIntro - AI-powered intro video generation with your avatar
 * Allows Ultra/Enterprise users to upload photos and generate 8-second intro videos
 * using Google Veo for real estate marketing.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Upload, X, Trash2, Download, Loader2, AlertCircle, Crown,
    Image as ImageIcon, Video, Play, Pause, User, Star,
    CheckCircle, ChevronDown, Settings, Eye, Camera,
    Plus, Edit2, Check, Sparkles, Film, Clock, RefreshCw,
    Mic, MessageSquare, Car, Home, Building2, TreePine,
    Monitor, Sofa, ChefHat, Smartphone, Clapperboard
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import {
    VIDEO_SETTINGS,
    SETTING_CATEGORIES,
    AVATAR_SCRIPTS,
    SCRIPT_CATEGORIES,
    VIDEO_STATUS,
    VIDEO_CREDITS_COST,
    MAX_AVATARS,
    MAX_SCRIPT_LENGTH,
    VIDEO_DURATION_SECONDS,
    uploadAvatar,
    listAvatars,
    getAvatar,
    updateAvatar,
    deleteAvatar,
    setDefaultAvatar,
    generateVideo,
    listVideos,
    getVideoStatus,
    deleteVideo,
    pollVideoCompletion,
    getScriptById,
    getSettingById
} from '../services/AvatarService';
import { fetchCreditBalance } from '../services/ChatUsageService';

// Setting icon mapping
const SETTING_ICONS = {
    studio_direct: Monitor,
    office_desk: Monitor,
    office_standing: Building2,
    walk_talk_sidewalk: User,
    walk_talk_neighborhood: Home,
    front_yard: TreePine,
    car_parked: Car,
    car_driving: Car,
    open_house_door: Home,
    living_room: Sofa,
    kitchen_island: ChefHat,
    backyard_patio: TreePine,
    selfie_style: Smartphone,
    drone_approach: Camera,
    green_screen: Clapperboard
};

/**
 * Format credits for display
 */
const formatCredits = (credits) => {
    if (credits === null || credits === undefined) return '...';
    if (credits >= 1000) {
        return `${(credits / 1000).toFixed(1)}k`;
    }
    return credits.toLocaleString();
};

/**
 * Avatar Upload Modal Component
 */
const AvatarUploadModal = ({ isOpen, onClose, onSuccess, existingCount }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [name, setName] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = useCallback((e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(selectedFile.type)) {
            setError('Please select a JPEG, PNG, or WebP image');
            return;
        }

        // Validate file size (10MB)
        if (selectedFile.size > 10 * 1024 * 1024) {
            setError('Image must be under 10MB');
            return;
        }

        setFile(selectedFile);
        setError(null);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(selectedFile);
    }, []);

    const handleUpload = async () => {
        if (!file) return;

        setIsUploading(true);
        setError(null);

        try {
            const result = await uploadAvatar(file, name || null);
            onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreview(null);
        setName('');
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-slate-700/50 shadow-xl"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Camera className="w-5 h-5 text-teal-400" />
                        Upload Avatar Photo
                    </h3>
                    <button onClick={handleClose} className="p-1 hover:bg-slate-700 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {existingCount >= MAX_AVATARS ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                        <div className="flex gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-amber-200 font-medium">Maximum avatars reached</p>
                                <p className="text-amber-300/70 text-sm mt-1">
                                    You can have up to {MAX_AVATARS} avatars. Delete an existing avatar to add a new one.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Upload Area */}
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                                preview
                                    ? 'border-teal-500/50 bg-teal-500/5'
                                    : 'border-slate-600 hover:border-teal-500/50 hover:bg-slate-700/30'
                            }`}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleFileSelect}
                                className="hidden"
                            />

                            {preview ? (
                                <div className="space-y-4">
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-teal-500/30"
                                    />
                                    <p className="text-sm text-slate-300">Click to change photo</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="w-16 h-16 bg-slate-700/50 rounded-full mx-auto flex items-center justify-center">
                                        <Upload className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">Drop your photo here</p>
                                        <p className="text-sm text-slate-400 mt-1">or click to browse</p>
                                    </div>
                                    <p className="text-xs text-slate-500">JPEG, PNG, or WebP • Max 10MB</p>
                                </div>
                            )}
                        </div>

                        {/* Name Input */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Avatar Name (optional)
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={`Avatar ${existingCount + 1}`}
                                maxLength={100}
                                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
                            />
                        </div>

                        {/* Tips */}
                        <div className="mt-4 bg-slate-700/30 rounded-lg p-3">
                            <p className="text-xs text-slate-400">
                                <strong className="text-slate-300">Tips for best results:</strong>
                                <br />• Use a clear, front-facing photo
                                <br />• Good lighting helps AI generate better videos
                                <br />• Professional headshots work great
                            </p>
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                <p className="text-sm text-red-400 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleClose}
                                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={!file || isUploading}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Upload Avatar
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
};

AvatarUploadModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    existingCount: PropTypes.number.isRequired
};

/**
 * Video Generation Modal Component
 */
const VideoGenerationModal = ({ isOpen, onClose, avatar, onSuccess, credits }) => {
    const [selectedSetting, setSelectedSetting] = useState(null);
    const [selectedScript, setSelectedScript] = useState(null);
    const [customScript, setCustomScript] = useState('');
    const [useCustomScript, setUseCustomScript] = useState(false);
    const [settingCategory, setSettingCategory] = useState('studio');
    const [scriptCategory, setScriptCategory] = useState('listing');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStatus, setGenerationStatus] = useState(null);
    const [error, setError] = useState(null);

    const filteredSettings = VIDEO_SETTINGS.filter(s => s.category === settingCategory);
    const filteredScripts = AVATAR_SCRIPTS.filter(s => s.category === scriptCategory);

    const canGenerate = selectedSetting && (selectedScript || (useCustomScript && customScript.trim()));
    const hasEnoughCredits = credits >= VIDEO_CREDITS_COST;

    const handleGenerate = async () => {
        if (!canGenerate || !hasEnoughCredits) return;

        setIsGenerating(true);
        setError(null);
        setGenerationStatus({ stage: 'starting', message: 'Starting video generation...' });

        try {
            const result = await generateVideo(
                avatar.id,
                selectedSetting,
                useCustomScript ? null : selectedScript,
                useCustomScript ? customScript.trim() : null
            );

            setGenerationStatus({ stage: 'processing', message: 'AI is creating your video...', job: result.job });

            // Poll for completion
            const completed = await pollVideoCompletion(
                result.job.id,
                (status) => {
                    setGenerationStatus({
                        stage: status.status,
                        message: status.status === 'processing'
                            ? 'AI is creating your video...'
                            : 'Finalizing...',
                        job: status
                    });
                }
            );

            setGenerationStatus({ stage: 'completed', message: 'Video ready!', job: completed });
            onSuccess(completed);
        } catch (err) {
            setError(err.message);
            setGenerationStatus(null);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleClose = () => {
        if (isGenerating) return; // Prevent closing during generation
        setSelectedSetting(null);
        setSelectedScript(null);
        setCustomScript('');
        setUseCustomScript(false);
        setGenerationStatus(null);
        setError(null);
        onClose();
    };

    if (!isOpen || !avatar) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-800 rounded-2xl max-w-3xl w-full mx-4 border border-slate-700/50 shadow-xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
                    <div className="flex items-center gap-4">
                        <img
                            src={avatar.thumbnail_url || avatar.photo_url}
                            alt={avatar.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-teal-500/30"
                        />
                        <div>
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Video className="w-5 h-5 text-teal-400" />
                                Generate Intro Video
                            </h3>
                            <p className="text-sm text-slate-400">Using {avatar.name}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isGenerating}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Video Setting Selection */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-3">
                            1. Choose Video Setting
                        </label>

                        {/* Setting Categories */}
                        <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                            {SETTING_CATEGORIES.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSettingCategory(cat.id)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                        settingCategory === cat.id
                                            ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 border border-transparent'
                                    }`}
                                >
                                    {cat.icon} {cat.name}
                                </button>
                            ))}
                        </div>

                        {/* Settings Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {filteredSettings.map(setting => {
                                const SettingIcon = SETTING_ICONS[setting.id] || Settings;
                                return (
                                    <button
                                        key={setting.id}
                                        onClick={() => setSelectedSetting(setting.id)}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            selectedSetting === setting.id
                                                ? 'bg-teal-500/10 border-teal-500/50 ring-1 ring-teal-500/30'
                                                : 'bg-slate-700/30 border-slate-600/50 hover:border-slate-500'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <SettingIcon className="w-4 h-4 text-teal-400" />
                                            <span className="text-sm font-medium text-white">{setting.name}</span>
                                        </div>
                                        <p className="text-xs text-slate-400 line-clamp-2">{setting.description}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Script Selection */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-slate-300">
                                2. Choose What to Say
                            </label>
                            <button
                                onClick={() => setUseCustomScript(!useCustomScript)}
                                className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
                                    useCustomScript
                                        ? 'bg-teal-500/20 text-teal-400'
                                        : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                                }`}
                            >
                                {useCustomScript ? '← Use Preset' : 'Write Custom →'}
                            </button>
                        </div>

                        {useCustomScript ? (
                            <div>
                                <textarea
                                    value={customScript}
                                    onChange={(e) => setCustomScript(e.target.value.slice(0, MAX_SCRIPT_LENGTH))}
                                    placeholder="Write your custom intro script..."
                                    rows={3}
                                    className="w-full bg-slate-700/50 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 resize-none"
                                />
                                <div className="flex justify-between mt-1">
                                    <p className="text-xs text-slate-500">Keep it short for an 8-second video</p>
                                    <p className={`text-xs ${customScript.length >= MAX_SCRIPT_LENGTH ? 'text-amber-400' : 'text-slate-500'}`}>
                                        {customScript.length}/{MAX_SCRIPT_LENGTH}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Script Categories */}
                                <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                                    {SCRIPT_CATEGORIES.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setScriptCategory(cat.id)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                                scriptCategory === cat.id
                                                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 border border-transparent'
                                            }`}
                                        >
                                            {cat.icon} {cat.name}
                                        </button>
                                    ))}
                                </div>

                                {/* Scripts Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                    {filteredScripts.map(script => (
                                        <button
                                            key={script.id}
                                            onClick={() => setSelectedScript(script.id)}
                                            className={`p-3 rounded-xl border text-left transition-all ${
                                                selectedScript === script.id
                                                    ? 'bg-cyan-500/10 border-cyan-500/50 ring-1 ring-cyan-500/30'
                                                    : 'bg-slate-700/30 border-slate-600/50 hover:border-slate-500'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">{script.icon}</span>
                                                <span className="text-sm text-white">"{script.text}"</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Generation Status with Progress Bar */}
                    {generationStatus && (
                        <div className={`rounded-xl p-5 ${
                            generationStatus.stage === 'completed'
                                ? 'bg-emerald-500/10 border border-emerald-500/30'
                                : 'bg-slate-700/50 border border-slate-600/50'
                        }`}>
                            {/* Progress Steps */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    {['Starting', 'Processing', 'Rendering', 'Complete'].map((step, idx) => {
                                        const stages = ['starting', 'processing', 'pending', 'completed'];
                                        const currentIdx = stages.indexOf(generationStatus.stage);
                                        const isComplete = idx < currentIdx || generationStatus.stage === 'completed';
                                        const isCurrent = idx === currentIdx && generationStatus.stage !== 'completed';

                                        return (
                                            <div key={step} className="flex flex-col items-center flex-1">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-all ${
                                                    isComplete
                                                        ? 'bg-emerald-500 text-white'
                                                        : isCurrent
                                                            ? 'bg-teal-500 text-white animate-pulse'
                                                            : 'bg-slate-600 text-slate-400'
                                                }`}>
                                                    {isComplete ? (
                                                        <Check className="w-4 h-4" />
                                                    ) : isCurrent ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <span className="text-xs">{idx + 1}</span>
                                                    )}
                                                </div>
                                                <span className={`text-xs ${
                                                    isComplete || isCurrent ? 'text-white' : 'text-slate-500'
                                                }`}>{step}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Progress Bar */}
                                <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                                    <motion.div
                                        className={`h-full ${generationStatus.stage === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-teal-500 to-cyan-500'}`}
                                        initial={{ width: '0%' }}
                                        animate={{
                                            width: generationStatus.stage === 'completed' ? '100%'
                                                : generationStatus.stage === 'processing' ? '60%'
                                                : generationStatus.stage === 'pending' ? '80%'
                                                : '25%'
                                        }}
                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                    />
                                </div>
                            </div>

                            {/* Status Message */}
                            <div className="flex items-center gap-3">
                                {generationStatus.stage === 'completed' ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                                ) : (
                                    <Loader2 className="w-5 h-5 text-teal-400 animate-spin" />
                                )}
                                <div className="flex-1">
                                    <p className={`font-medium ${
                                        generationStatus.stage === 'completed' ? 'text-emerald-300' : 'text-teal-300'
                                    }`}>
                                        {generationStatus.stage === 'completed'
                                            ? '✨ Your video is ready!'
                                            : generationStatus.stage === 'processing'
                                                ? 'AI is generating your video...'
                                                : generationStatus.stage === 'pending'
                                                    ? 'Finalizing video...'
                                                    : 'Starting video generation...'}
                                    </p>
                                    {generationStatus.stage !== 'completed' && (
                                        <p className="text-xs text-slate-400 mt-1">
                                            This usually takes 1-2 minutes. Please don't close this window.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                            <p className="text-sm text-red-400 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                {error}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-slate-700/50 bg-slate-800/50">
                    <div className="flex items-center gap-2 text-sm">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span className="text-slate-400">Cost:</span>
                        <span className="text-white font-medium">{VIDEO_CREDITS_COST.toLocaleString()} credits</span>
                        {!hasEnoughCredits && (
                            <span className="text-red-400 text-xs ml-2">(Insufficient credits)</span>
                        )}
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isGenerating}
                            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            {generationStatus?.stage === 'completed' ? 'Close' : 'Cancel'}
                        </button>
                        {generationStatus?.stage !== 'completed' && (
                            <button
                                onClick={handleGenerate}
                                disabled={!canGenerate || !hasEnoughCredits || isGenerating}
                                className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isGenerating ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" />
                                        Generate Video
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

VideoGenerationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    avatar: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
    credits: PropTypes.number.isRequired
};

/**
 * Main AvatarIntro Component
 */
const AvatarIntro = () => {
    // State
    const [avatars, setAvatars] = useState([]);
    const [videos, setVideos] = useState([]);
    const [credits, setCredits] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedAvatar, setSelectedAvatar] = useState(null);

    // UI states
    const [activeTab, setActiveTab] = useState('avatars'); // avatars | videos
    const [playingVideoId, setPlayingVideoId] = useState(null);

    // Load data
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const [avatarsData, videosData, creditsData] = await Promise.all([
                listAvatars().catch(() => ({ avatars: [], count: 0 })),
                listVideos().catch(() => ({ videos: [], count: 0 })),
                fetchCreditBalance().catch(() => ({ aiCreditsRemaining: 0 }))
            ]);

            setAvatars(avatarsData.avatars || []);
            setVideos(videosData.videos || []);
            setCredits(creditsData.credits_balance ?? creditsData.aiCreditsRemaining ?? 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle avatar upload success
    const handleAvatarUploaded = (newAvatar) => {
        setAvatars(prev => [newAvatar, ...prev]);
        setShowUploadModal(false);
    };

    // Handle video generation success
    const handleVideoGenerated = (video) => {
        setVideos(prev => [video, ...prev]);
        setShowVideoModal(false);
        setSelectedAvatar(null);
        // Refresh credits
        fetchCreditBalance().then(data => {
            setCredits(data.aiCreditsRemaining ?? 0);
        }).catch(() => {});
    };

    // Handle set default avatar
    const handleSetDefault = async (avatarId) => {
        try {
            await setDefaultAvatar(avatarId);
            setAvatars(prev => prev.map(a => ({
                ...a,
                is_default: a.id === avatarId
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    // Handle delete avatar
    const handleDeleteAvatar = async (avatarId) => {
        if (!window.confirm('Delete this avatar? This cannot be undone.')) return;

        try {
            await deleteAvatar(avatarId);
            setAvatars(prev => prev.filter(a => a.id !== avatarId));
        } catch (err) {
            setError(err.message);
        }
    };

    // Handle delete video
    const handleDeleteVideo = async (videoId) => {
        if (!window.confirm('Delete this video? This cannot be undone.')) return;

        try {
            await deleteVideo(videoId);
            setVideos(prev => prev.filter(v => v.id !== videoId));
        } catch (err) {
            setError(err.message);
        }
    };

    // Generate video with avatar
    const handleGenerateVideo = (avatar) => {
        setSelectedAvatar(avatar);
        setShowVideoModal(true);
    };

    // Download video
    const handleDownloadVideo = (video) => {
        if (!video.video_url) return;
        const link = document.createElement('a');
        link.href = video.video_url;
        link.download = `avatar-intro-${video.id}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Loading avatars...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <Video className="w-5 h-5 text-white" />
                        </div>
                        Avatar Intro Videos
                    </h1>
                    <p className="text-slate-400 mt-1">
                        Generate AI-powered 8-second intro videos with your photo
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Credits Display */}
                    <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/80 rounded-xl border border-slate-700/50">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span className="text-white font-medium">{formatCredits(credits)}</span>
                        <span className="text-slate-400 text-sm">credits</span>
                    </div>

                    {/* Add Avatar Button */}
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-xl font-medium transition-all flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Avatar
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <p className="text-red-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </p>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-700/50 pb-1">
                <button
                    onClick={() => setActiveTab('avatars')}
                    className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                        activeTab === 'avatars'
                            ? 'bg-slate-700/50 text-white border-b-2 border-teal-500'
                            : 'text-slate-400 hover:text-white'
                    }`}
                >
                    <User className="w-4 h-4 inline mr-2" />
                    My Avatars ({avatars.length}/{MAX_AVATARS})
                </button>
                <button
                    onClick={() => setActiveTab('videos')}
                    className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                        activeTab === 'videos'
                            ? 'bg-slate-700/50 text-white border-b-2 border-teal-500'
                            : 'text-slate-400 hover:text-white'
                    }`}
                >
                    <Film className="w-4 h-4 inline mr-2" />
                    Generated Videos ({videos.length})
                </button>
            </div>

            {/* Avatars Tab */}
            {activeTab === 'avatars' && (
                <div>
                    {avatars.length === 0 ? (
                        <div className="text-center py-16 bg-slate-800/30 rounded-2xl border border-slate-700/30">
                            <div className="w-16 h-16 bg-slate-700/50 rounded-full mx-auto mb-4 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-slate-500" />
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">No avatars yet</h3>
                            <p className="text-slate-400 mb-6 max-w-md mx-auto">
                                Upload a photo of yourself to start generating AI intro videos for your listings.
                            </p>
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-xl font-medium transition-all inline-flex items-center gap-2"
                            >
                                <Upload className="w-4 h-4" />
                                Upload Your First Avatar
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {avatars.map(avatar => (
                                <div
                                    key={avatar.id}
                                    className="group bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden hover:border-teal-500/30 transition-all"
                                >
                                    {/* Avatar Image */}
                                    <div className="relative aspect-square">
                                        <img
                                            src={avatar.photo_url || avatar.thumbnail_url}
                                            alt={avatar.name}
                                            className="w-full h-full object-cover"
                                        />
                                        {avatar.is_default && (
                                            <div className="absolute top-2 left-2 px-2 py-0.5 bg-teal-500 rounded-full text-xs text-white font-medium flex items-center gap-1">
                                                <Star className="w-3 h-3" />
                                                Default
                                            </div>
                                        )}

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleGenerateVideo(avatar)}
                                                className="p-2 bg-teal-500 rounded-lg hover:bg-teal-400 transition-colors"
                                                title="Generate Video"
                                            >
                                                <Video className="w-5 h-5 text-white" />
                                            </button>
                                            {!avatar.is_default && (
                                                <button
                                                    onClick={() => handleSetDefault(avatar.id)}
                                                    className="p-2 bg-slate-600 rounded-lg hover:bg-slate-500 transition-colors"
                                                    title="Set as Default"
                                                >
                                                    <Star className="w-5 h-5 text-white" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDeleteAvatar(avatar.id)}
                                                className="p-2 bg-red-500/80 rounded-lg hover:bg-red-500 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-5 h-5 text-white" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Avatar Info */}
                                    <div className="p-3">
                                        <h3 className="font-medium text-white truncate">{avatar.name}</h3>
                                        <p className="text-xs text-slate-400 mt-1">
                                            {new Date(avatar.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))}

                            {/* Add More Button */}
                            {avatars.length < MAX_AVATARS && (
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="aspect-square bg-slate-800/30 rounded-2xl border-2 border-dashed border-slate-600 hover:border-teal-500/50 hover:bg-slate-800/50 transition-all flex flex-col items-center justify-center gap-3"
                                >
                                    <div className="w-12 h-12 bg-slate-700/50 rounded-full flex items-center justify-center">
                                        <Plus className="w-6 h-6 text-slate-400" />
                                    </div>
                                    <span className="text-sm text-slate-400">Add Avatar</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Videos Tab */}
            {activeTab === 'videos' && (
                <div>
                    {videos.length === 0 ? (
                        <div className="text-center py-16 bg-slate-800/30 rounded-2xl border border-slate-700/30">
                            <div className="w-16 h-16 bg-slate-700/50 rounded-full mx-auto mb-4 flex items-center justify-center">
                                <Film className="w-8 h-8 text-slate-500" />
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">No videos yet</h3>
                            <p className="text-slate-400 mb-6 max-w-md mx-auto">
                                Generate your first intro video by selecting an avatar and choosing a script.
                            </p>
                            {avatars.length > 0 ? (
                                <button
                                    onClick={() => handleGenerateVideo(avatars[0])}
                                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-xl font-medium transition-all inline-flex items-center gap-2"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    Generate Your First Video
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-xl font-medium transition-all inline-flex items-center gap-2"
                                >
                                    <Upload className="w-4 h-4" />
                                    Upload an Avatar First
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {videos.map(video => {
                                const setting = getSettingById(video.setting);
                                const script = video.script_id ? getScriptById(video.script_id) : null;

                                return (
                                    <div
                                        key={video.id}
                                        className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden"
                                    >
                                        {/* Video Player */}
                                        <div className="relative aspect-video bg-slate-900">
                                            {video.status === VIDEO_STATUS.COMPLETED && video.video_url ? (
                                                <video
                                                    src={video.video_url}
                                                    className="w-full h-full object-cover"
                                                    controls={playingVideoId === video.id}
                                                    poster={video.thumbnail_url}
                                                    onPlay={() => setPlayingVideoId(video.id)}
                                                    onPause={() => setPlayingVideoId(null)}
                                                    onEnded={() => setPlayingVideoId(null)}
                                                />
                                            ) : video.status === VIDEO_STATUS.PROCESSING ? (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="text-center">
                                                        <Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-2" />
                                                        <p className="text-sm text-slate-400">Processing...</p>
                                                    </div>
                                                </div>
                                            ) : video.status === VIDEO_STATUS.FAILED ? (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="text-center">
                                                        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                                        <p className="text-sm text-red-400">Generation failed</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
                                                </div>
                                            )}

                                            {/* Play Button Overlay */}
                                            {video.status === VIDEO_STATUS.COMPLETED && video.video_url && playingVideoId !== video.id && (
                                                <button
                                                    onClick={() => setPlayingVideoId(video.id)}
                                                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                                                >
                                                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                                        <Play className="w-6 h-6 text-white ml-1" />
                                                    </div>
                                                </button>
                                            )}

                                            {/* Duration Badge */}
                                            <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 rounded text-xs text-white">
                                                {VIDEO_DURATION_SECONDS}s
                                            </div>
                                        </div>

                                        {/* Video Info */}
                                        <div className="p-4">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="text-white font-medium line-clamp-1">
                                                        "{video.script_text}"
                                                    </p>
                                                    <p className="text-xs text-slate-400 mt-1">
                                                        {setting?.name || video.setting} • {new Date(video.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex gap-2 mt-3">
                                                {video.status === VIDEO_STATUS.COMPLETED && video.video_url && (
                                                    <button
                                                        onClick={() => handleDownloadVideo(video)}
                                                        className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        Download
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteVideo(video.id)}
                                                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            <AnimatePresence>
                {showUploadModal && (
                    <AvatarUploadModal
                        isOpen={showUploadModal}
                        onClose={() => setShowUploadModal(false)}
                        onSuccess={handleAvatarUploaded}
                        existingCount={avatars.length}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showVideoModal && selectedAvatar && (
                    <VideoGenerationModal
                        isOpen={showVideoModal}
                        onClose={() => {
                            setShowVideoModal(false);
                            setSelectedAvatar(null);
                        }}
                        avatar={selectedAvatar}
                        onSuccess={handleVideoGenerated}
                        credits={credits || 0}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AvatarIntro;
